import catalog from "./topicPages.json";

/** Published topic pages must clear this length. The placeholder does not. */
export const TOPIC_MIN_WORDS = 300;

export type TopicSection = {
  heading: string;
  paragraphs: string[];
};

export type TopicFaq = {
  question: string;
  answer: string;
};

export type TopicPractice = {
  grade: number;
  level: "elementary" | "middle" | "high";
  subject: string;
  skill: string;
};

export type TopicLocaleCopy = {
  title: string;
  description: string;
  keywords: string;
  heading: string;
  /** Short label for same-language topic navigation. */
  navLabel?: string;
  practiceLabel?: string;
  faqHeading?: string;
  sections: TopicSection[];
  faqs?: TopicFaq[];
};

export type HubCopy = {
  title: string;
  description: string;
  keywords: string;
  heading: string;
  paragraphs: string[];
};

export type TopicRecord = {
  slug: string;
  published: boolean;
  /** Hebrew topic copy stays unpublished until a person reviews it. */
  heReviewed: boolean;
  practice?: TopicPractice;
  en: TopicLocaleCopy;
  he: TopicLocaleCopy;
};

/** Short primary action. The longer practiceLabel stays on the link at the bottom. */
export function practiceNowLabel(lang: "en" | "he"): string {
  return lang === "he" ? "תרגלו עכשיו" : "Practice now";
}

export function practiceHomePath(lang: "en" | "he"): string {
  return lang === "he" ? "/he/practice" : "/practice";
}

export function topicPracticeHref(
  topic: TopicRecord,
  lang: "en" | "he",
): string {
  const practice = topic.practice;
  if (!practice) return "";
  const params = new URLSearchParams();
  params.set("grade", String(practice.grade));
  params.set("level", practice.level);
  params.set("subject", practice.subject);
  if (practice.skill) params.set("skill", practice.skill);
  const base = lang === "he" ? "/he/practice" : "/practice";
  return `${base}?${params.toString()}`;
}

const topics = catalog.topics as TopicRecord[];
const hub = catalog.hub as { en: HubCopy; he: HubCopy };

export function hubCopy(lang: "en" | "he"): HubCopy {
  return hub[lang];
}

export function mathHubPath(lang: "en" | "he"): string {
  return lang === "he" ? "/he/math" : "/math";
}

/** Topics that are allowed into the index and the public topic lists. */
export function publishedTopics(): TopicRecord[] {
  return topics.filter((topic) => topic.published && topic.heReviewed);
}

export function mathTopicPath(slug: string, lang: "en" | "he"): string {
  return lang === "he" ? `/he/math/${slug}` : `/math/${slug}`;
}

export function topicBySlug(slug: string): TopicRecord | null {
  return topics.find((topic) => topic.slug === slug) ?? null;
}

export function topicFromPath(
  pathname: string,
): { topic: TopicRecord; lang: "en" | "he" } | null {
  const match = pathname.match(/^\/(?:(he)\/)?math\/([^/]+)$/);
  if (!match) return null;
  const topic = topicBySlug(match[2]);
  if (!topic) return null;
  return { topic, lang: match[1] === "he" ? "he" : "en" };
}

export function isMathTopicPath(pathname: string): boolean {
  return /^\/(?:he\/)?math\/[^/]+$/.test(pathname);
}
